// src/features/aspects/logic.ts
// Framework-agnostic business logic for Aspects tab.
// Zero React imports — pure TypeScript functions and data maps.

import type { Aspect } from '@core/types/task/Aspect';
import type { AspectCardData, AspectTypeConfig } from './types';

// ---------------------------------------------------------------------------
// Core aspect types (hardcoded; replaced by BFF manifest in Phase 1.2)
// ---------------------------------------------------------------------------

export const CORE_ASPECT_TYPES: AspectTypeConfig[] = [
  {
    typeName: 'task',
    displayNameKey: 'rail.aspects.type.task.name',
    descriptionKey: 'rail.aspects.type.task.description',
    actionVerbKey: 'rail.aspects.type.task.verb',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    color: 'text-blue-500',
    category: 'productivity',
    previewFields: ['status', 'assignee', 'due_date'],
    requiredFields: [],
    matchEntityTypes: ['document', 'invoice', 'incident', 'project'],
    excludeEntityTypes: ['task'],
    priority: 0,
  },
  {
    typeName: 'document',
    displayNameKey: 'rail.aspects.type.document.name',
    descriptionKey: 'rail.aspects.type.document.description',
    actionVerbKey: 'rail.aspects.type.document.verb',
    icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
    color: 'text-emerald-500',
    category: 'content',
    previewFields: ['version', 'status'],
    requiredFields: [],
    matchEntityTypes: ['invoice', 'task', 'project'],
    excludeEntityTypes: ['document'],
    priority: 1,
  },
  {
    typeName: 'calendar',
    displayNameKey: 'rail.aspects.type.calendar.name',
    descriptionKey: 'rail.aspects.type.calendar.description',
    actionVerbKey: 'rail.aspects.type.calendar.verb',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    color: 'text-orange-500',
    category: 'scheduling',
    previewFields: ['start_time', 'end_time'],
    requiredFields: [],
    matchEntityTypes: ['task', 'booking', 'project'],
    excludeEntityTypes: ['calendar'],
    priority: 2,
  },
  {
    typeName: 'booking',
    displayNameKey: 'rail.aspects.type.booking.name',
    descriptionKey: 'rail.aspects.type.booking.description',
    actionVerbKey: 'rail.aspects.type.booking.verb',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    color: 'text-purple-500',
    category: 'commerce',
    previewFields: ['slot', 'price'],
    requiredFields: [],
    matchEntityTypes: ['property', 'resource', 'service'],
    excludeEntityTypes: ['booking'],
    priority: 3,
  },
];

// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------

export function toAspectCardData(aspect: Aspect): AspectCardData {
  const typeName = extractTypeName(aspect);
  return {
    id: aspect.entity?.id || '',
    typeId: aspect.entity?.type_id || '',
    urn: aspect.urn || '',
    typeName,
    title: aspect.title || 'Untitled',
    subtitle: aspect.subtitle || '',
    icon: aspect.icon || getAspectTypeIcon(typeName),
    color: aspect.color || getAspectTypeColor(typeName),
    status: aspect.status || '',
    statusVariant: getAspectStatusVariant(aspect.status),
    updatedAt: aspect.updated_at ? String(aspect.updated_at) : '',
    previewFields: {},
  };
}

function extractTypeName(aspect: Aspect): string {
  // Type encoded in entity.type_name: "task_aspect" → "task"
  const typeName = aspect.entity?.type_name || '';
  return typeName.replace(/_aspect$/, '') || 'unknown';
}

function getAspectStatusVariant(status: string): string {
  if (!status) return 'secondary';
  const lower = status.toLowerCase();
  if (lower.includes('active') || lower.includes('open') || lower.includes('in_progress')) return 'primary';
  if (lower.includes('done') || lower.includes('completed')) return 'success';
  if (lower.includes('blocked') || lower.includes('cancelled')) return 'error';
  if (lower.includes('pending') || lower.includes('draft')) return 'secondary';
  return 'default';
}

// ---------------------------------------------------------------------------
// Recommendations
// ---------------------------------------------------------------------------

export function getRecommendedAspectTypes(
  entityType: string,
  allTypes: AspectTypeConfig[],
): AspectTypeConfig[] {
  return allTypes
    .filter(
      (t) =>
        t.matchEntityTypes.includes(entityType) &&
        !t.excludeEntityTypes.includes(entityType),
    )
    .sort((a, b) => a.priority - b.priority);
}

export function getAvailableAspectTypes(
  allTypes: AspectTypeConfig[],
  attachedTypeNames: string[],
): AspectTypeConfig[] {
  const attached = new Set(attachedTypeNames);
  return allTypes.filter((t) => !attached.has(t.typeName));
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

export function sortAspects(aspects: Aspect[]): Aspect[] {
  return [...aspects].sort((a, b) => {
    const aTime = a.updated_at ? new Date(String(a.updated_at)).getTime() : 0;
    const bTime = b.updated_at ? new Date(String(b.updated_at)).getTime() : 0;
    return bTime - aTime;
  });
}

// ---------------------------------------------------------------------------
// Type display helpers
// ---------------------------------------------------------------------------

export function getAspectTypeIcon(typeName: string): string {
  const config = CORE_ASPECT_TYPES.find((t) => t.typeName === typeName);
  return config?.icon || 'M4 6h16M4 12h16M4 18h16';
}

export function getAspectTypeColor(typeName: string): string {
  const config = CORE_ASPECT_TYPES.find((t) => t.typeName === typeName);
  return config?.color || 'text-muted-foreground';
}

export function getAspectStatusConfig(
  _typeName: string,
  status: string,
): { variant: string; labelKey: string } {
  // Generic mapping — profile services can override via manifest
  const variant = getAspectStatusVariant(status);
  return { variant, labelKey: `rail.aspects.status.${status.toLowerCase()}` };
}

// ---------------------------------------------------------------------------
// Category grouping
// ---------------------------------------------------------------------------

export function groupTypesByCategory(
  types: AspectTypeConfig[],
): [string, AspectTypeConfig[]][] {
  const groups = new Map<string, AspectTypeConfig[]>();
  for (const t of types) {
    const existing = groups.get(t.category) || [];
    existing.push(t);
    groups.set(t.category, existing);
  }
  return Array.from(groups.entries());
}
